import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BadgeCheck, MapPin, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * USPS-verified address autocomplete (Smarty).
 * Controlled component — caller owns `value` and decides when to persist.
 *
 * value shape:
 * { address_line1, address_line2, city, state, zip,
 *   address_verified, address_provider, address_zip4, address_lat, address_lng,
 *   address_cass, address_geocode_precision }
 *
 * Phase 4a Step 3: adds a "Verify this address" affordance that runs the
 * verify-typed path against `/api/address/verify-typed`. Appears when the
 * dropdown has nothing to offer (e.g., Smarty plan without Autocomplete Pro,
 * or the user typed past the typeahead window) AND the parent has provided
 * city/state/zip in `value`. Same response handling as the suggestion path.
 */
export default function AddressAutocomplete({ value, onChange }) {
  const [query, setQuery] = useState(value?.address_line1 || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [editing, setEditing] = useState(!value?.address_verified);
  const [verifyingTyped, setVerifyingTyped] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  // Phase 4a Step 4 — auto-verify pipeline refs.
  //   verifyDebounceRef: 800ms idle timer for auto-verify (SEPARATE
  //     from the 200ms `debounceRef` used for typeahead fetches).
  //   lastAttemptedQueryRef: the trimmed line1 string of the last
  //     verify attempt (success OR failure). Auto/blur paths skip
  //     re-verifying an unchanged query; the manual button bypasses.
  const verifyDebounceRef = useRef(null);
  const lastAttemptedQueryRef = useRef("");
  const verifyTypedRef = useRef(null);

  useEffect(() => {
    if (value?.address_verified) {
      setEditing(false);
      setQuery(value.address_line1 || "");
    }
  }, [value?.address_verified, value?.address_line1]);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const { data } = await api.get("/address/autocomplete", { params: { q } });
      setSuggestions(data || []);
      setOpen(true);
      setHighlight(-1);
    } catch (e) {
      // silently fail — the user can still hand-edit
      setSuggestions([]);
    }
  }, []);

  const handleQueryChange = (q) => {
    setQuery(q);
    setEditing(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 200);
  };

  const pick = async (s) => {
    setOpen(false);
    try {
      const { data } = await api.post("/address/verify", { suggestion_id: s.id });
      const next = {
        address_line1: data.address_line1,
        address_line2: value?.address_line2 || data.address_line2 || "",
        city: data.city,
        state: data.state,
        zip: data.zip,
        address_verified: true,
        address_provider: data.provider,
        address_zip4: data.zip4 || null,
        address_lat: data.lat || null,
        address_lng: data.lng || null,
        // Phase 4a Step 2 — surfaced from live Smarty; mock returns null.
        address_cass: data.address_cass || null,
        address_geocode_precision: data.address_geocode_precision || null,
      };
      setQuery(data.address_line1);
      lastAttemptedQueryRef.current = (data.address_line1 || "").trim();
      setEditing(false);
      onChange?.(next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleKey = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const editAddress = () => {
    setEditing(true);
    onChange?.({
      ...value,
      address_verified: false,
      address_provider: null,
      address_zip4: null,
      address_lat: null,
      address_lng: null,
    });
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const handleApt = (v) => onChange?.({ ...value, address_line2: v });

  // Phase 4a Step 3 — show the "Verify this address" button when:
  //  - the user is in editing mode (no verified card yet)
  //  - the dropdown is currently empty (live Smarty plan w/o Autocomplete Pro,
  //    or the user typed past the typeahead window)
  //  - the user has typed something meaningful in line1 (≥4 chars)
  //  - city/state/zip in the parent's `value` are all populated
  const canVerifyTyped = useMemo(() => {
    if (!editing) return false;
    if (open && suggestions.length > 0) return false;
    if (!query || query.trim().length < 4) return false;
    const hasCity = !!(value?.city || "").trim();
    const hasState = !!(value?.state || "").trim();
    const hasZip = /^\d{5}$/.test((value?.zip || "").trim());
    return hasCity && hasState && hasZip;
  }, [editing, open, suggestions.length, query, value?.city, value?.state, value?.zip]);

  // Phase 4a Step 4 — preconditions for verify-typed WITHOUT the
  // "picker is open" gate. Blur bypasses the picker gate because the
  // picker is about to close; the debounced auto-verify still respects
  // the picker gate via `canVerifyTyped` above.
  const meetsVerifyPreconditions = useMemo(() => {
    if (!editing) return false;
    if (!query || query.trim().length < 4) return false;
    const hasCity = !!(value?.city || "").trim();
    const hasState = !!(value?.state || "").trim();
    const hasZip = /^\d{5}$/.test((value?.zip || "").trim());
    return hasCity && hasState && hasZip;
  }, [editing, query, value?.city, value?.state, value?.zip]);

  // Phase 4a Step 4 — debounced auto-verify. Fires 800ms after the
  // user stops changing any signal that flips `canVerifyTyped`.
  // Skips when the picker is showing (user should pick a suggestion
  // instead), when a verify is already in-flight, or when the
  // current query matches the last attempt (idempotent — see the
  // `verifyTypedRef` capture pattern below). `verifyTypedRef` is
  // used so the timer always calls the freshest closure (verifyTyped
  // is redeclared on every render).
  useEffect(() => {
    verifyTypedRef.current = verifyTyped;
  });

  useEffect(() => {
    if (verifyDebounceRef.current) {
      clearTimeout(verifyDebounceRef.current);
      verifyDebounceRef.current = null;
    }
    if (!canVerifyTyped) return;
    if ((query || "").trim() === lastAttemptedQueryRef.current) return;
    verifyDebounceRef.current = setTimeout(() => {
      verifyDebounceRef.current = null;
      if (verifyTypedRef.current) verifyTypedRef.current();
    }, 800);
    return () => {
      if (verifyDebounceRef.current) {
        clearTimeout(verifyDebounceRef.current);
        verifyDebounceRef.current = null;
      }
    };
    // `query` is the primary keystroke signal; `canVerifyTyped`
    // captures city/state/zip/editing/open transitions.
  }, [canVerifyTyped, query]);

  const verifyTyped = async () => {
    // Guards: no double-fire while in flight, no attempt on an
    // empty line1. `verifyTyped` itself does NOT gate on
    // `lastAttemptedQueryRef` — that skip lives in the auto-verify
    // + blur callsites so the manual "Verify this address" button
    // can always retry (Phase 4a Step 4).
    if (verifyingTyped) return;
    const trimmedQ = (query || "").trim();
    if (!trimmedQ) return;
    setVerifyingTyped(true);
    lastAttemptedQueryRef.current = trimmedQ;
    try {
      const { data } = await api.post("/address/verify-typed", {
        address_line1: query.trim(),
        address_line2: (value?.address_line2 || "").trim() || null,
        city: (value?.city || "").trim(),
        state: (value?.state || "").trim().toUpperCase(),
        zip: (value?.zip || "").trim(),
      });
      const next = {
        address_line1: data.address_line1,
        address_line2: value?.address_line2 || data.address_line2 || "",
        city: data.city,
        state: data.state,
        zip: data.zip,
        address_verified: true,
        address_provider: data.provider,
        address_zip4: data.zip4 || null,
        address_lat: data.lat || null,
        address_lng: data.lng || null,
        address_cass: data.address_cass || null,
        address_geocode_precision: data.address_geocode_precision || null,
      };
      setQuery(data.address_line1);
      lastAttemptedQueryRef.current = (data.address_line1 || "").trim();
      setEditing(false);
      setOpen(false);
      onChange?.(next);
      toast.success("Address verified");
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      // 404 → mock didn't match / typed address not in dataset.
      // 422 → live Smarty marked it undeliverable.
      // Show whichever message the server provided; fall back to friendly text.
      if (status === 404) {
        toast.error(detail || "We couldn't find that address. Double-check the spelling and ZIP.");
      } else if (status === 422) {
        toast.error(detail || "USPS reports that address as not deliverable.");
      } else {
        toast.error(detail || e.message);
      }
    } finally {
      setVerifyingTyped(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="address-autocomplete">
      <div className="relative">
        <Label className="text-sm font-semibold text-pp-text">Address</Label>
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          onBlur={() => {
            setTimeout(() => setOpen(false), 120);
            // Phase 4a Step 4 — blur forces immediate auto-verify.
            // Cancel any pending 800ms debounce (blur wins the
            // race) then fire only when preconditions are met AND
            // the query has changed since the last attempt. The
            // "skip if unchanged" guard sits here (not inside
            // `verifyTyped`) so the manual "Verify this address"
            // button can still retry a failed attempt.
            if (verifyDebounceRef.current) {
              clearTimeout(verifyDebounceRef.current);
              verifyDebounceRef.current = null;
            }
            if (!meetsVerifyPreconditions) return;
            if (verifyingTyped) return;
            if ((query || "").trim() === lastAttemptedQueryRef.current) return;
            verifyTyped();
          }}
          onKeyDown={handleKey}
          placeholder="Start typing your street address…"
          className="mt-2 h-12 rounded-full px-5"
          data-testid="address-input"
          autoComplete="off"
        />
        {open && suggestions.length > 0 && (
          <ul
            className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-2xl border border-border bg-pp-surface py-1 shadow-md"
            data-testid="address-suggestions"
          >
            {suggestions.map((s, i) => (
              <li
                key={s.id}
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(s);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  "flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm",
                  i === highlight ? "bg-brand-orange/10 text-pp-text" : "text-foreground"
                )}
                data-testid={`address-suggestion-${i}`}
              >
                <MapPin className="h-4 w-4 shrink-0 text-brand-orange" />
                <span className="truncate">{s.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Phase 4a Step 3 — verify-typed affordance.
          Appears when the typeahead has nothing to offer and the user
          has filled out line1 + city/state/zip. Wires to the new
          POST /api/address/verify-typed endpoint, which the mock and
          live providers both implement. */}
      {canVerifyTyped && (
        <div
          className="flex items-center justify-between gap-3 rounded-2xl border border-dashed border-brand-orange/40 bg-brand-orange/[0.04] p-3"
          data-testid="address-verify-typed-card"
        >
          <p className="text-xs text-muted-foreground">
            No matches in the typeahead? Confirm your address against USPS records.
          </p>
          <Button
            type="button"
            onClick={verifyTyped}
            disabled={verifyingTyped}
            size="sm"
            className="shrink-0 rounded-full bg-pp-orange px-4 text-xs font-semibold text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid="address-verify-typed-button"
          >
            {verifyingTyped ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Verifying…
              </>
            ) : (
              "Verify this address"
            )}
          </Button>
        </div>
      )}

      {/* Verified summary */}
      {value?.address_verified && !editing && (
        <div
          className="flex items-start justify-between gap-3 rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-4"
          data-testid="address-verified-card"
        >
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange">
              <BadgeCheck className="h-4 w-4" />
              USPS-verified
            </span>
            <p className="mt-1 text-sm font-semibold text-pp-text">
              {value.address_line1}
              {value.address_line2 ? `, ${value.address_line2}` : ""}
            </p>
            <p className="text-sm text-muted-foreground">
              {value.city}, {value.state} {value.zip}
              {value.address_zip4 ? `-${value.address_zip4}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={editAddress}
            className="inline-flex items-center gap-1 self-start rounded-full px-3 py-1 text-xs font-semibold text-pp-text hover:bg-brand-orange/10 hover:text-brand-orange"
            data-testid="address-edit-link"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        </div>
      )}

      <div>
        <Label className="text-sm font-semibold text-pp-text">Apt / Suite (optional)</Label>
        <Input
          value={value?.address_line2 || ""}
          onChange={(e) => handleApt(e.target.value)}
          placeholder="Apt 2, Suite 410, etc."
          className="mt-2 h-12 rounded-full px-5"
          data-testid="address-line2-input"
        />
      </div>
    </div>
  );
}
